import type { SubQuiz } from "@ai-teach/types";
import { Button, IconButton } from "@ai-teach/ui-web";
import { useState } from "react";
import toast from "react-hot-toast";
import { HiChevronDown, HiChevronRight, HiPlus, HiX } from "react-icons/hi";
import { MdCallSplit } from "react-icons/md";

interface SubQuizSectionProps {
  quizzes: SubQuiz[];
  onChange: (next: SubQuiz[]) => void;
  /** Section heading, e.g. "Post-reading quizzes" or "Post-content quizzes". */
  label: string;
  /**
   * When provided, each quiz shows an "eject" action that turns it back into a standalone "other"
   * slide. The parent wires this to create the slide and drop the quiz from `quizzes`. Omitted where
   * ejecting isn't supported.
   */
  onEject?: (quiz: SubQuiz) => void;
}

/**
 * Post-content mini-quiz authoring, shared by reading fluency slides and "other" slides. These
 * quizzes live inside the parent slide's `slideData.subQuizzes` (not separate slides), so the
 * caller is responsible for wiring `onChange` to its own `updateSlide` dispatch. Question shape
 * mirrors the phonics-encode question: `correctAnswer` is separate and `options` holds the
 * distractors.
 */
export function SubQuizSection({ quizzes, onChange, label, onEject }: SubQuizSectionProps) {
  const [lastAddedId, setLastAddedId] = useState<string | null>(null);

  const updateQuiz = (id: string, patch: Partial<SubQuiz>) => {
    onChange(quizzes.map((q) => (q.id === id ? { ...q, ...patch } : q)));
  };

  const handleAddQuiz = () => {
    const newQuiz: SubQuiz = {
      id: crypto.randomUUID(),
      question: "",
      questionMode: "multiple_choice",
      options: [],
      correctAnswer: "",
    };
    setLastAddedId(newQuiz.id);
    onChange([...quizzes, newQuiz]);
    toast.success("Quiz question added!");
  };

  const handleRemoveQuiz = (id: string) => {
    onChange(quizzes.filter((q) => q.id !== id));
  };

  return (
    <div className="flex flex-col gap-2 pb-2">
      <div className="flex items-center gap-2 pt-1">
        <span className="text-[11px] font-semibold text-gray-600 uppercase tracking-wide">
          {label}
        </span>
        <span className="text-[10px] text-gray-400">{quizzes.length}</span>
        <div className="flex-1 h-px bg-gray-200" />
      </div>

      {quizzes.map((quiz, index) => (
        <QuizBlock
          key={quiz.id}
          quiz={quiz}
          index={index}
          onChange={(patch) => updateQuiz(quiz.id, patch)}
          onRemove={() => handleRemoveQuiz(quiz.id)}
          onEject={onEject ? () => onEject(quiz) : undefined}
          defaultExpanded={quiz.id === lastAddedId}
        />
      ))}

      <Button
        variant="secondary"
        size="sm"
        onClick={handleAddQuiz}
        className="w-full gap-1 text-xs"
      >
        <HiPlus />
        <span>Add Quiz Question</span>
      </Button>
    </div>
  );
}

interface QuizBlockProps {
  quiz: SubQuiz;
  index: number;
  onChange: (patch: Partial<SubQuiz>) => void;
  onRemove: () => void;
  onEject?: (() => void) | undefined;
  defaultExpanded?: boolean;
}

function QuizBlock({
  quiz,
  index,
  onChange,
  onRemove,
  onEject,
  defaultExpanded = false,
}: QuizBlockProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  const isMultipleChoice = quiz.questionMode === "multiple_choice";

  const updateOption = (optIndex: number, value: string) => {
    onChange({ options: quiz.options.map((o, i) => (i === optIndex ? value : o)) });
  };

  const addOption = () => onChange({ options: [...quiz.options, ""] });
  const removeOption = (optIndex: number) =>
    onChange({ options: quiz.options.filter((_, i) => i !== optIndex) });

  return (
    <div className="flex flex-col gap-2 rounded-lg border border-gray-200 bg-gray-50 p-2">
      {/* Header */}
      <div className="flex items-center justify-between">
        <button
          type="button"
          onClick={() => setIsExpanded((v) => !v)}
          className="flex items-center gap-1.5 min-w-0 flex-1 text-left"
        >
          <span className="text-gray-400 shrink-0">
            {isExpanded ? (
              <HiChevronDown className="text-sm" />
            ) : (
              <HiChevronRight className="text-sm" />
            )}
          </span>
          <h4 className="text-[11px] font-semibold text-gray-600 uppercase tracking-wide shrink-0">
            Quiz {index + 1}
          </h4>
          {!isExpanded && (
            <span className="text-[11px] text-gray-400 truncate">
              {quiz.question.trim() || "No question yet"}
            </span>
          )}
        </button>
        <div className="flex items-center gap-1">
          {onEject && (
            <IconButton
              variant="ghost"
              size="sm"
              onClick={onEject}
              className="!p-0.5"
              title="Eject into a separate 'Other' slide"
              aria-label="Eject into a separate slide"
            >
              <MdCallSplit className="text-xs" />
            </IconButton>
          )}
          <button
            type="button"
            onClick={onRemove}
            aria-label="Remove quiz"
            className="shrink-0 p-1 rounded text-muted-foreground/40 hover:text-red-500 hover:bg-red-50 transition-colors"
          >
            <HiX className="text-sm" />
          </button>
        </div>
      </div>

      {isExpanded && (
        <>
          {/* Question */}
          <textarea
            value={quiz.question}
            onChange={(e) => onChange({ question: e.target.value })}
            className="outline-none resize-none rounded-md border border-gray-100 bg-white px-2.5 py-1.5 text-xs leading-relaxed text-gray-800 placeholder:text-gray-300"
            placeholder="Enter the question…"
            style={{ height: 56 }}
          />

          {/* Mode toggle */}
          <div className="flex gap-1">
            {(
              [
                ["multiple_choice", "Multiple choice"],
                ["fill_in_blank", "Fill in blank"],
              ] as const
            ).map(([mode, label]) => (
              <button
                key={mode}
                type="button"
                onClick={() => onChange({ questionMode: mode })}
                className={`flex-1 text-[10px] py-1 rounded-md transition-colors font-medium ${
                  quiz.questionMode === mode
                    ? "bg-primary-100 text-primary-700"
                    : "bg-gray-100 text-gray-500 hover:bg-gray-200"
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Correct answer */}
          <div className="flex flex-col gap-1">
            <span className="text-[9px] font-semibold text-emerald-600 uppercase tracking-wider">
              Correct answer
            </span>
            <input
              value={quiz.correctAnswer}
              onChange={(e) => onChange({ correctAnswer: e.target.value })}
              className="outline-none rounded-md border border-emerald-200 bg-white px-2.5 py-1.5 text-xs text-gray-800 placeholder:text-gray-300"
              placeholder="The correct answer…"
            />
          </div>

          {/* Distractor options — multiple choice only */}
          {isMultipleChoice && (
            <div className="flex flex-col gap-1">
              <span className="text-[9px] font-semibold text-gray-500 uppercase tracking-wider">
                Distractors (wrong answers)
              </span>
              {quiz.options.map((opt, optIndex) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: options are positional and freely edited
                <div key={optIndex} className="flex items-center gap-1">
                  <input
                    value={opt}
                    onChange={(e) => updateOption(optIndex, e.target.value)}
                    className="flex-1 outline-none rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-xs text-gray-800 placeholder:text-gray-300"
                    placeholder={`Distractor ${optIndex + 1}`}
                  />
                  <button
                    type="button"
                    onClick={() => removeOption(optIndex)}
                    aria-label="Remove distractor"
                    className="shrink-0 p-1 rounded text-muted-foreground/40 hover:text-red-500 hover:bg-red-50 transition-colors"
                  >
                    <HiX className="text-sm" />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={addOption}
                className="flex items-center justify-center gap-1 text-[10px] py-1 rounded-md bg-slate-50 text-slate-600 hover:bg-slate-100 transition-colors font-medium"
              >
                <HiPlus className="text-[10px]" />
                Add distractor
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
